import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, HeartIcon, ImagePlusIcon, MinusIcon, PlusIcon } from "lucide-react";
import type { Product } from "../../types/product";
import { useCart } from "../../contexts/CartContext";
import { colorSwatches, formatPrice } from "../../utils/format";
import { btnPrimary, ease } from "../../utils/styles";
export function PurchasePanel({
  product
}: {
  product: Product;
}) {
  const {
    add,
    wishlist,
    toggleWish
  } = useCart();
  const [color, setColor] = useState<string | undefined>(product.colors?.[0]);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [added, setAdded] = useState(false);
  const wished = wishlist.includes(product.id);
  useEffect(() => {
    setColor(product.colors?.[0]);
    setQty(1);
    setNote("");
    setFileName("");
    setError("");
  }, [product]);
  const submit = () => {
    if (product.personalization === "text" && !note.trim()) {
      setError("Vul de naam of tekst in die op het product moet komen.");
      return;
    }
    if (product.personalization === "photo" && !fileName) {
      setError("Upload de foto die we voor je lamp gebruiken.");
      return;
    }
    add(product, {
      color,
      qty,
      note: product.personalization === "text" ? note.trim() : fileName || undefined
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };
  const field = "w-full rounded-2xl bg-sand px-4 text-[15px] text-ink transition-colors duration-150 focus:bg-paper focus:outline-none focus:ring-2 focus:ring-ink";
  return <div data-id="element-295" data-mp="3fav4yijnshcm">
      <div className="flex items-center gap-2" data-id="element-296" data-mp="3fb50elnh3gpp">
        <span className="rounded-full bg-sand px-3 py-1 text-xs text-ink" data-id="element-297" data-mp="3f9adx2wjzgji">{product.type}</span>
        {product.badge && <span className="rounded-full bg-gold-soft/40 px-3 py-1 text-xs text-gold-deep" data-id="element-298" data-mp="2nk76xfda7s36">{product.badge}</span>}
      </div>
      <h1 className="mt-4 text-4xl font-medium tracking-[-0.04em] text-ink sm:text-5xl" data-id="element-299" data-mp="qk4f0926hmml">{product.name}</h1>
      <p className="mt-1 text-[16px] text-muted" data-id="element-300" data-mp="vhw4yxj9iyo5">{product.title}</p>
      <p className="mt-5 text-2xl text-ink" data-id="element-301" data-mp="109e4dh5n0r70">
        {product.priceFrom && <span className="text-base text-muted" data-id="element-302" data-mp="10a61iwevuwze">vanaf </span>}
        {formatPrice(product.price)}
      </p>
      <p className="mt-5 text-[15px] leading-relaxed text-ink/70" data-id="element-303" data-mp="157pii42ytnv1">{product.description}</p>

      <div className="mt-8 space-y-6" data-id="element-304" data-mp="7mjnsvbxybyt">
        {product.colors && product.colors.length > 0 && <fieldset data-id="element-305" data-mp="ck1zuul997b1">
            <legend className="text-sm text-muted" data-id="element-306" data-mp="clmrabc9wa4o">
              Kleur · <span className="text-ink" data-id="element-307" data-mp="hi3dv13tqgfn">{color}</span>
            </legend>
            <div className="mt-3 flex flex-wrap gap-3" data-id="element-308" data-mp="mi9bqz0osz66">
              {product.colors.map(c => <button key={c} type="button" onClick={() => setColor(c)} aria-pressed={color === c} aria-label={c} title={c} className={`h-10 w-10 rounded-full transition-shadow duration-150 ${color === c ? "ring-2 ring-ink ring-offset-2" : "ring-1 ring-ink/10 hover:ring-ink/30"}`} style={{
            backgroundColor: colorSwatches[c]
          }} data-id="element-309" data-mp="1syusw3q4znxy" />)}
            </div>
          </fieldset>}

        {product.personalization === "text" && <label className="block" data-id="element-310" data-mp="2ys95k3p84915">
            <span className="flex justify-between text-sm text-muted" data-id="element-311" data-mp="2yp3ggoglh75f">
              Naam of tekst <span data-id="element-312" data-mp="2uo1j6d68n563">{note.length}/20</span>
            </span>
            <input value={note} maxLength={20} onChange={e => {
          setNote(e.target.value);
          setError("");
        }} placeholder="Bijv. Antoine" className={`${field} mt-2 h-12 ${error ? "ring-2 ring-red-600" : ""}`} data-id="element-313" data-mp="2ppq67tkncejn" />
          </label>}

        {product.personalization === "photo" && <div data-id="element-314" data-mp="3ct3289d18fie">
            <span className="text-sm text-muted" data-id="element-315" data-mp="3csjlgvz4bx5y">Jouw foto</span>
            <label className={`mt-2 flex cursor-pointer items-center gap-3 rounded-2xl bg-sand px-4 py-4 text-sm transition-colors duration-150 hover:bg-stone ${error ? "ring-2 ring-red-600" : ""}`} data-id="element-316" data-mp="3gtwsh9dy66eg">
              <ImagePlusIcon className="h-5 w-5 text-gold-deep" data-id="element-317" data-mp="37s5n65nbk1l4" />
              <span className="flex-1 truncate text-ink/80" data-id="element-318" data-mp="2t42hyyh3otxd">{fileName || "Upload een foto (JPG of PNG)"}</span>
              <input type="file" accept="image/*" className="sr-only" onChange={e => {
            setFileName(e.target.files?.[0]?.name ?? "");
            setError("");
          }} data-id="element-319" data-mp="2xuivsjou2hk9" />
            </label>
          </div>}

        {error && <p className="text-sm text-red-700" role="alert" data-id="element-320" data-mp="2a5q5p3uw73xk">
            {error}
          </p>}

        <div className="flex gap-2" data-id="element-321" data-mp="2f3rm1h0xop5c">
          <div className="flex h-14 items-center rounded-full bg-sand" data-id="element-322" data-mp="1vihnhj361yc3">
            <button type="button" onClick={() => setQty(q => Math.max(1, q - 1))} className="flex h-14 w-12 items-center justify-center rounded-full" aria-label="Minder" data-id="element-323" data-mp="1vkkf27jyvzix">
              <MinusIcon className="h-4 w-4" data-id="element-324" data-mp="25hgp4hpiktpl" />
            </button>
            <span className="w-6 text-center text-sm tabular-nums" aria-live="polite" data-id="element-325" data-mp="20h1cxtgyxs97">
              {qty}
            </span>
            <button type="button" onClick={() => setQty(q => q + 1)} className="flex h-14 w-12 items-center justify-center rounded-full" aria-label="Meer" data-id="element-326" data-mp="2eg11z28e895w">
              <PlusIcon className="h-4 w-4" data-id="element-327" data-mp="2o6vqkd943kui" />
            </button>
          </div>
          <button type="button" onClick={submit} className={`${btnPrimary} h-14 flex-1 overflow-hidden`} data-id="element-328" data-mp="2o8zpshw2fvog">
            <AnimatePresence mode="wait" initial={false} data-id="element-329" data-mp="2y5ck9hzfqdk9">
              <motion.span key={added ? "added" : "add"} initial={{
              y: 14,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} exit={{
              y: -14,
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease
            }} className="inline-flex items-center gap-2" data-id="element-330" data-mp="2y5unwzoffu27">
                {added ? <>
                    <CheckIcon className="h-4 w-4" data-id="element-331" data-mp="3a6ueuo4d3ge0" /> Toegevoegd
                  </> : <>In winkelwagen · {formatPrice(product.price * qty)}</>}
              </motion.span>
            </AnimatePresence>
          </button>
          <button type="button" onClick={() => toggleWish(product.id)} aria-pressed={wished} aria-label={wished ? "Van verlanglijst verwijderen" : "Aan verlanglijst toevoegen"} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-sand transition-[background-color,transform] duration-150 ease-out hover:bg-stone active:scale-90" data-id="element-332" data-mp="3ny98rpzt51zu">
            <HeartIcon className={`h-5 w-5 ${wished ? "fill-gold text-gold" : ""}`} data-id="element-333" data-mp="23jpe1i3v2ktm" />
          </button>
        </div>
      </div>
    </div>;
}